import { IntlShape } from 'react-intl';

export type EvaluationValue = 0 | 1 | 2 | 3 | 4 | 5;

export type EvaluationBackendState =
  | 'unreviewed'
  | 'considered'
  | 'conditional'
  | 'selected'
  | 'rejected'
  | 'priority';

export type EvaluationFilterValue =
  | 'all'
  | 'unreviewed'
  | 'considered'
  | 'conditional'
  | 'priority'
  | 'selected'
  | 'rejected';

export type EvaluationStarReviewDashboard = {
  total_reviews: number;
  total_ratings: number;
  avg_star_rating: number;
  star_1_count: number;
  star_2_count: number;
  star_3_count: number;
  star_4_count: number;
  star_5_count: number;
  total_comment: number;
  last_review_time: number;
  last_calculated_time: number;
};

export type EvaluationOfferLike = {
  id: number;
  state?: number;
  created_by_name?: string;
  contract_id: number;
  share_key_id?: number | string;

  offeror_identity_information?: {
    name?: string;
    email?: string;
    company?: string;
  } | null;

  vendor_information?: {
    company_name?: string | null;
    contact_name?: string | null;
    contact_email?: string | null;
    company_main_email?: string | null;
  } | null;

  screening?: {
    state?: EvaluationValue | EvaluationBackendState | null;
    is_accepted?: boolean | null;
  } | null;

  evaluation?: {
    state?: EvaluationValue | EvaluationBackendState | null;
    evaluation_star_review_dashboard?: EvaluationStarReviewDashboard | null;
  } | null;

  score?: {
    delivery_current?: number | null;
    delivery_max?: number | null;
    optional_current?: number | null;
    optional_max?: number | null;
  } | null;
};

export type EvaluationContractLike = {
  id: number;
  name?: string;
  description?: string | null;
  reference?: string | null;
  state?: number;
};

export type EvaluationVoucherLike = {
  id: number;
  tender_voucher_id?: number;
  name?: string;
  description?: string | null;
  voucher_type?: number;
  is_optional?: boolean;
  attachments?: any[];
  unit_count?: number | null;
  unit_type?: number | null;
};

export type EvaluationVoucherResponseLike = {
  voucher_id?: number;
  voucher_type?: number;
  sum?: number | null;
  is_approved?: boolean | null;
  attachments?: any[];
};

export type EvaluationMergedVoucher = {
  id: number;
  name: string;
  description?: string | null;
  voucherType?: number;
  isOptional: boolean;
  isDelivered: boolean;
  projectAttachments: any[];
  vendorAttachments: any[];
  unitCount?: number | null;
  unitType?: number | null;
  response?: EvaluationVoucherResponseLike;
};

export type EvaluationScore = {
  delivered: number;
  total: number;
};

export type EvaluationScores = {
  required: EvaluationScore;
  optional: EvaluationScore;
};

export const EVALUATION_BACKEND_STATE_BY_VALUE: Record<
  EvaluationValue,
  EvaluationBackendState
> = {
  0: 'unreviewed',
  1: 'considered',
  2: 'conditional',
  3: 'selected',
  4: 'rejected',
  5: 'priority',
};

export const getEvaluationBackendState = (
  value: EvaluationValue
): EvaluationBackendState => {
  return EVALUATION_BACKEND_STATE_BY_VALUE[value] || 'unreviewed';
};

export const EVALUATION_OPTIONS: {
  value: EvaluationValue;
  intlId: string;
  defaultLabel: string;
  color: string;
  background: string;
}[] = [
  {
    value: 0,
    intlId: 'evaluation-status-unreviewed',
    defaultLabel: 'Unreviewed',
    color: '#8c8c8c',
    background: '#fafafa',
  },
  {
    value: 1,
    intlId: 'evaluation-status-considered',
    defaultLabel: 'Considered',
    color: '#1890ff',
    background: '#e6f7ff',
  },
  {
    value: 2,
    intlId: 'evaluation-status-conditional',
    defaultLabel: 'Conditional',
    color: '#13c2c2',
    background: '#e6fffb',
  },
  {
    value: 3,
    intlId: 'evaluation-status-selected',
    defaultLabel: 'Selected',
    color: '#52c41a',
    background: '#f6ffed',
  },
  {
    value: 5,
    intlId: 'evaluation-status-priority',
    defaultLabel: 'Priority',
    color: '#7cb305',
    background: '#fcffe6',
  },
  {
    value: 4,
    intlId: 'evaluation-status-rejected',
    defaultLabel: 'Rejected',
    color: '#ff4d4f',
    background: '#fff1f0',
  },
];

export const EVALUATION_FILTER_OPTIONS: {
  value: EvaluationFilterValue;
  intlId: string;
  defaultLabel: string;
}[] = [
  {
    value: 'all',
    intlId: 'evaluation-filter-all-deliveries',
    defaultLabel: 'All deliveries',
  },
  {
    value: 'unreviewed',
    intlId: 'evaluation-status-unreviewed',
    defaultLabel: 'Unreviewed',
  },
  {
    value: 'considered',
    intlId: 'evaluation-status-considered',
    defaultLabel: 'Considered',
  },
  {
    value: 'conditional',
    intlId: 'evaluation-status-conditional',
    defaultLabel: 'Conditional',
  },
  {
    value: 'selected',
    intlId: 'evaluation-status-selected',
    defaultLabel: 'Selected',
  },
  {
    value: 'priority',
    intlId: 'evaluation-status-priority',
    defaultLabel: 'Priority',
  },
  {
    value: 'rejected',
    intlId: 'evaluation-status-rejected',
    defaultLabel: 'Rejected',
  },
];

export const getEvaluationValue = (
  offer: EvaluationOfferLike
): EvaluationValue => {
  const evaluationState = offer.evaluation?.state;

  if (evaluationState === 1 || evaluationState === 'considered') {
    return 1;
  }

  if (evaluationState === 2 || evaluationState === 'conditional') {
    return 2;
  }

  if (evaluationState === 3 || evaluationState === 'selected') {
    return 3;
  }

  if (evaluationState === 4 || evaluationState === 'rejected') {
    return 4;
  }

  if (evaluationState === 5 || evaluationState === 'priority') {
    return 5;
  }

  return 0;
};

export const getEvaluationOption = (value?: EvaluationValue | null) => {
  return (
    EVALUATION_OPTIONS.find((option) => option.value === value) ||
    EVALUATION_OPTIONS[0]
  );
};

export const getEvaluationFilterValue = (
  value?: EvaluationValue | null
): EvaluationFilterValue => {
  switch (value) {
    case 1:
      return 'considered';
    case 2:
      return 'conditional';
    case 3:
      return 'selected';
    case 4:
      return 'rejected';
    case 5:
      return 'priority';
    case 0:
    default:
      return 'unreviewed';
  }
};

export const getOfferEvaluationScores = (
  offer: EvaluationOfferLike
): EvaluationScores => {
  return {
    required: {
      delivered: Number(offer.score?.delivery_current ?? 0),
      total: Number(offer.score?.delivery_max ?? 0),
    },
    optional: {
      delivered: Number(offer.score?.optional_current ?? 0),
      total: Number(offer.score?.optional_max ?? 0),
    },
  };
};

export const getOfferVendorName = (
  offer: EvaluationOfferLike,
  intl: IntlShape
) => {
  return (
    offer.vendor_information?.contact_name ||
    offer.offeror_identity_information?.name ||
    offer.created_by_name ||
    intl.formatMessage({ id: 'offers-offer-number' }, { id: offer.id })
  );
};

export const getOfferVendorCompany = (offer: EvaluationOfferLike) => {
  return (
    offer.vendor_information?.company_name ||
    offer.offeror_identity_information?.company ||
    '-'
  );
};

export const getOfferVendorEmail = (offer: EvaluationOfferLike) => {
  return (
    offer.vendor_information?.contact_email ||
    offer.vendor_information?.company_main_email ||
    offer.offeror_identity_information?.email ||
    '-'
  );
};

export const getVoucherId = (voucher: EvaluationVoucherLike) => {
  return Number(voucher.tender_voucher_id ?? voucher.id);
};

export const isVoucherDelivered = (
  voucher: EvaluationVoucherLike,
  response?: EvaluationVoucherResponseLike
) => {
  if (!response) return false;

  const voucherType = voucher.voucher_type;

  if (voucherType === 1) {
    return response.sum !== null && response.sum !== undefined;
  }

  if (voucherType === 3) {
    return !!response.attachments?.length;
  }

  if (voucherType === 4) {
    return response.is_approved !== null && response.is_approved !== undefined;
  }

  return (
    response.sum !== null ||
    response.is_approved !== null ||
    !!response.attachments?.length
  );
};

export const getDeliveryScoreColor = (score: EvaluationScore) => {
  if (score.total === 0) return '#8c8c8c';

  if (score.delivered === score.total) {
    return '#52c41a';
  }

  if (score.delivered === 0) {
    return '#ff4d4f';
  }

  return '#faad14';
};

export const buildMergedEvaluationVouchers = (
  vouchers: EvaluationVoucherLike[],
  responses: EvaluationVoucherResponseLike[],
  intl: IntlShape
): EvaluationMergedVoucher[] => {
  const responsesByVoucherId = new Map<number, EvaluationVoucherResponseLike>();

  (responses || []).forEach((response) => {
    if (response?.voucher_id !== null && response?.voucher_id !== undefined) {
      responsesByVoucherId.set(Number(response.voucher_id), response);
    }
  });

  return (vouchers || []).map((voucher) => {
    const voucherId = getVoucherId(voucher);
    const response = responsesByVoucherId.get(voucherId);

    return {
      id: voucherId,
      name:
        voucher.name ||
        intl.formatMessage({ id: 'offers-voucher-number' }, { id: voucherId }),
      description: voucher.description,
      voucherType: voucher.voucher_type,
      isOptional: !!voucher.is_optional,
      isDelivered: isVoucherDelivered(voucher, response),
      projectAttachments: voucher.attachments || [],
      vendorAttachments: response?.attachments || [],
      unitCount: voucher.unit_count,
      unitType: voucher.unit_type,
      response,
    };
  });
};

/**
 * Compute stable serials for offers within a contract.
 * Serials are 1-indexed and based on offer.id ascending.
 */
export const computeOfferSerials = (
  offers: { id: number }[]
): Map<number, string> => {
  const sorted = [...offers].sort((a, b) => a.id - b.id);
  const map = new Map<number, string>();
  sorted.forEach((offer, index) => {
    const serial = String(index + 1).padStart(3, '0');
    map.set(offer.id, `#${serial}`);
  });
  return map;
};

/**
 * Get serial for an offer, or fallback if not found.
 */
export const getOfferSerial = (
  offerId: number,
  serialMap: Map<number, string>
): string => {
  return serialMap.get(offerId) || `#${String(offerId).padStart(3, '0')}`;
};
